import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Tooltip, Typography, useMediaQuery } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import type { CalendarAssignment, CalendarEvent, CellIndex, StaffSummary } from '../../hooks/useCalendar';
import { eventCellKey } from '../../hooks/useCalendar';
import { formatDayNum, formatDow, formatShort, isWeekend, type IsoDate } from '../../lib/dates';

/**
 * The calendar: jobs down the side, days across the top, staff in the cells.
 *
 * This matches how the office actually schedules — you open a job and fill its
 * crew for each day, rather than opening a person and filling their week. It is
 * the shape of the existing screen, kept deliberately.
 *
 * What changes underneath is everything the old screen got wrong: one row per
 * job instead of one database row per person-per-day with the job copied onto
 * each, a real foreign key to staff instead of a name string, and no way to
 * write the same person onto the same job twice on the same day.
 *
 * Staff chips are coloured by the staff member's SKILLSET (staff_title), not by
 * the job — the same person reads the same colour everywhere on the board.
 */

interface JobGridProps {
    readonly dates: readonly IsoDate[];
    readonly events: readonly CalendarEvent[];
    readonly cells: CellIndex;
    readonly staffById: ReadonlyMap<number, StaffSummary>;
    readonly categoryNames: ReadonlyMap<number, string>;
    /** staffId -> dates that person is on more than one job. */
    readonly clashes: Readonly<Record<string, readonly IsoDate[]>>;
    readonly selection: ReadonlySet<string>;
    readonly onSelectionChange: (next: ReadonlySet<string>) => void;
    readonly onAddStaff: (eventId: number, date: IsoDate) => void;
    readonly onRemoveAssignment: (assignment: CalendarAssignment) => void;
    /** Drop a staff chip onto another job/day. */
    readonly onMoveAssignment: (assignment: CalendarAssignment, toEventId: number, toDate: IsoDate) => void;
    readonly onEditEvent: (event: CalendarEvent) => void;
    readonly today: IsoDate;
    /**
     * Phones get the board to look at only. Scheduling a crew across a grid
     * this size is a desk job, so on a phone nothing can be selected, added,
     * moved, removed or opened for editing — and a finger scrolls the grid.
     */
    readonly readOnly?: boolean;
}

const JOB_COL = 260;
const MIN_ROW_H = 58;
const MIN_DAY_COL = 150;
/** Phone widths: at the desktop sizes the job column alone fills the screen. */
const JOB_COL_COMPACT = 120;
const MIN_DAY_COL_COMPACT = 96;
/** Chips drawn before the rest collapse into "+N", so one big crew cannot make every row tall. */
const MAX_CHIPS = 6;

const UNASSIGNED = '#6b7a8f';

/**
 * Treat whitespace as absent.
 *
 * Production has rows whose `address` is a single space, so a `!== ''` test
 * passes and the row renders with a blank title. Every text field off the legacy
 * MySQL is suspect this way — it had no NOT NULL and no trimming anywhere.
 */
function text(value: string | null): string | null {
    if (value === null) {
        return null;
    }
    const trimmed = value.trim();
    return trimmed === '' ? null : trimmed;
}

function jobTitle(event: CalendarEvent, categoryNames: ReadonlyMap<number, string>): string {
    const address = text(event.siteAddress);
    if (address !== null) {
        return address;
    }
    if (event.categoryId !== null) {
        return categoryNames.get(event.categoryId) ?? 'Leave';
    }
    const job = text(event.jobNumber);
    if (job !== null) {
        return `Job ${job}`;
    }
    return 'Untitled';
}

// ─── Cell ───────────────────────────────────────────────────────────────────

interface CellProps {
    readonly eventId: number;
    readonly date: IsoDate;
    readonly assignments: readonly CalendarAssignment[];
    readonly staffById: ReadonlyMap<number, StaffSummary>;
    readonly clashes: Readonly<Record<string, readonly IsoDate[]>>;
    readonly selected: boolean;
    readonly weekend: boolean;
    readonly inWindow: boolean;
    readonly onPointerDown: (eventId: number, date: IsoDate, additive: boolean) => void;
    readonly onPointerEnter: (eventId: number, date: IsoDate) => void;
    readonly minWidth: number;
    readonly readOnly: boolean;
    readonly onAddStaff: (eventId: number, date: IsoDate) => void;
    readonly onRemoveAssignment: (assignment: CalendarAssignment) => void;
    readonly onChipDragStart: (assignment: CalendarAssignment) => void;
    readonly onChipDrop: (eventId: number, date: IsoDate) => void;
    readonly dropTarget: boolean;
}

/**
 * Memoised, and that memo is load-bearing: dragging fires a pointerenter per
 * cell, so without it every cell on screen would re-render on every mouse move.
 */
const Cell = memo(function Cell({
    eventId,
    date,
    assignments,
    staffById,
    clashes,
    selected,
    weekend,
    inWindow,
    onPointerDown,
    onPointerEnter,
    minWidth,
    readOnly,
    onAddStaff,
    onRemoveAssignment,
    onChipDragStart,
    onChipDrop,
    dropTarget,
}: CellProps): React.JSX.Element {
    const theme = useTheme();

    return (
        <Box
            // Addresses the cell for tests and for the drop handler's own
            // debugging; the grid has no other stable handle on a job/day pair.
            data-cell={`${String(eventId)}|${date}`}
            onPointerDown={(e) => {
                if (readOnly) {
                    return;
                }
                // Deliberately no preventDefault(): cancelling the default action
                // on pointerdown also cancels the native HTML5 drag gesture, so
                // chips silently stopped being draggable. Text selection is
                // already suppressed by userSelect: 'none' on the grid container.
                onPointerDown(eventId, date, e.shiftKey || e.metaKey || e.ctrlKey);
            }}
            onPointerEnter={() => {
                onPointerEnter(eventId, date);
            }}
            onDragOver={(e) => {
                if (readOnly) {
                    return;
                }
                // Without preventDefault the browser refuses the drop outright.
                e.preventDefault();
            }}
            onDrop={(e) => {
                if (readOnly) {
                    return;
                }
                e.preventDefault();
                onChipDrop(eventId, date);
            }}
            sx={{
                position: 'relative',
                minWidth,
                minHeight: MIN_ROW_H,
                borderRight: '1px solid',
                borderBottom: '1px solid',
                borderColor: 'divider',
                bgcolor: selected
                    ? alpha(theme.palette.secondary.main, 0.3)
                    : weekend
                      ? alpha(theme.palette.common.black, 0.38)
                      : !inWindow
                        ? alpha(theme.palette.common.black, 0.2)
                        : 'transparent',
                boxShadow: dropTarget
                    ? `inset 0 0 0 2px ${theme.palette.info.light}`
                    : selected
                      ? `inset 0 0 0 2px ${theme.palette.secondary.main}`
                      : 'none',
                cursor: readOnly ? 'default' : 'cell',
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
                p: '5px',
                transition: 'background-color 90ms linear',
                // Read-only lets a finger scroll the grid; editable keeps every
                // touch for drag-select.
                touchAction: readOnly ? 'auto' : 'none',
                '&:hover .add-staff': { opacity: 1 },
            }}
        >
            {assignments.slice(0, MAX_CHIPS).map((a) => {
                const person = a.staffId === null ? undefined : staffById.get(a.staffId);
                const colour = person?.colour ?? UNASSIGNED;
                const clashDates = a.staffId === null ? undefined : clashes[String(a.staffId)];
                const clashing = clashDates?.includes(date) ?? false;

                return (
                    <Tooltip
                        key={a.id}
                        placement="top"
                        disableInteractive
                        title={
                            person === undefined
                                ? 'Unfilled crew slot'
                                : `${person.name}${person.titleName === null ? '' : ` · ${person.titleName}`}${clashing ? ' · also on another job today' : ''}`
                        }
                    >
                        <Box
                            draggable={!readOnly}
                            data-assignment={String(a.id)}
                            onDragStart={(e) => {
                                e.dataTransfer.effectAllowed = 'move';
                                // Firefox ignores a drag with no payload set.
                                e.dataTransfer.setData('text/plain', String(a.id));
                                onChipDragStart(a);
                            }}
                            // Deliberately NOT stopping propagation: a busy cell is
                            // almost entirely chips, so swallowing the press here
                            // left nowhere to begin a drag on exactly the rows that
                            // most need re-crewing. The press starts a selection like
                            // anywhere else.
                            //
                            // No onClick here either. Clicking the chip used to remove
                            // it, and a press that moved less than the browser's drag
                            // threshold is a click — so trying to drag someone deleted
                            // them. Removing is the x below, which only shows on hover.
                            sx={{
                                flex: '0 0 auto',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                px: 0.75,
                                height: 22,
                                borderRadius: 0.75,
                                bgcolor: person === undefined ? 'transparent' : colour,
                                border: person === undefined ? '1px dashed' : 'none',
                                borderColor: 'text.disabled',
                                color:
                                    person === undefined
                                        ? 'text.secondary'
                                        : (person.textColour ?? theme.palette.getContrastText(colour)),
                                fontSize: '0.74rem',
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: readOnly ? 'default' : 'grab',
                                '&:hover': { filter: 'brightness(1.15)' },
                                '&:hover .remove-chip': { display: readOnly ? 'none' : 'flex' },
                                '&:active': { cursor: 'grabbing' },
                            }}
                        >
                            {clashing ? <WarningAmberIcon sx={{ fontSize: 13, flex: '0 0 auto' }} /> : null}
                            <Box
                                component="span"
                                sx={{ flex: '1 1 auto', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            >
                                {person?.name ?? 'Unfilled'}
                            </Box>
                            <Box
                                className="remove-chip"
                                role="button"
                                aria-label={`Take ${person?.name ?? 'unfilled slot'} off this day`}
                                // Own press, not the cell's: this must not start a
                                // selection or a drag.
                                onPointerDown={(e) => {
                                    e.stopPropagation();
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onRemoveAssignment(a);
                                }}
                                sx={{
                                    display: 'none',
                                    flex: '0 0 auto',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    width: 16,
                                    height: 16,
                                    ml: 0.25,
                                    borderRadius: '50%',
                                    cursor: 'pointer',
                                    bgcolor: alpha(theme.palette.common.black, 0.25),
                                    '&:hover': { bgcolor: alpha(theme.palette.common.black, 0.5) },
                                }}
                            >
                                <CloseIcon sx={{ fontSize: 12 }} />
                            </Box>
                        </Box>
                    </Tooltip>
                );
            })}

            {assignments.length > MAX_CHIPS ? (
                <Tooltip
                    title={assignments
                        .slice(MAX_CHIPS)
                        .map((a) => (a.staffId === null ? 'Unfilled' : (staffById.get(a.staffId)?.name ?? '?')))
                        .join(', ')}
                >
                    <Box
                        sx={{
                            px: 0.75,
                            borderRadius: 0.75,
                            border: '1px dashed',
                            borderColor: 'text.disabled',
                            color: 'text.secondary',
                            fontSize: '0.68rem',
                            fontWeight: 700,
                            lineHeight: '17px',
                        }}
                    >
                        {`+${String(assignments.length - MAX_CHIPS)} more`}
                    </Box>
                </Tooltip>
            ) : null}

            {/* The equivalent of the old "Select Staff" box, but it only appears
                on hover so a full board is not half empty dropdowns. */}
            {readOnly ? null : (
            <Box
                className="add-staff"
                onPointerDown={(e) => {
                    e.stopPropagation();
                }}
                onClick={() => {
                    onAddStaff(eventId, date);
                }}
                sx={{
                    flex: '0 0 auto',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.25,
                    height: 20,
                    px: 0.5,
                    borderRadius: 0.75,
                    border: '1px dashed',
                    borderColor: 'divider',
                    color: 'text.disabled',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    cursor: 'pointer',
                    opacity: assignments.length === 0 ? 0.55 : 0,
                    transition: 'opacity 120ms',
                    '&:hover': { borderColor: 'secondary.main', color: 'secondary.main' },
                }}
            >
                <AddIcon sx={{ fontSize: 13 }} />
                Staff
            </Box>
            )}
        </Box>
    );
});

// ─── Grid ───────────────────────────────────────────────────────────────────

export function JobGrid({
    dates,
    events,
    cells,
    staffById,
    categoryNames,
    clashes,
    selection,
    onSelectionChange,
    onAddStaff,
    onRemoveAssignment,
    onMoveAssignment,
    onEditEvent,
    today,
    readOnly = false,
}: JobGridProps): React.JSX.Element {
    const theme = useTheme();
    const compact = useMediaQuery(theme.breakpoints.down('sm'));
    const jobCol = compact ? JOB_COL_COMPACT : JOB_COL;
    const dayCol = compact ? MIN_DAY_COL_COMPACT : MIN_DAY_COL;
    const dragging = useRef(false);
    const anchor = useRef<{ eventId: number; date: IsoDate } | null>(null);
    const baseSelection = useRef<ReadonlySet<string>>(new Set());
    /**
     * The chip currently being dragged to another cell.
     *
     * A ref, not state, because `drop` can arrive in the same tick as
     * `dragstart` — React had not flushed the state update yet, so the drop
     * handler read null and silently did nothing. The boolean beside it exists
     * only to re-render the drop-target outline.
     */
    const carrying = useRef<CalendarAssignment | null>(null);
    const [isCarrying, setIsCarrying] = useState(false);

    const rowIndex = useMemo(() => {
        const m = new Map<number, number>();
        events.forEach((e, i) => m.set(e.id, i));
        return m;
    }, [events]);

    /** Rectangular selection from the anchor to the cell under the pointer. */
    const extendTo = useCallback(
        (eventId: number, date: IsoDate) => {
            const a = anchor.current;
            if (a === null) {
                return;
            }
            const r1 = rowIndex.get(a.eventId);
            const r2 = rowIndex.get(eventId);
            const c1 = dates.indexOf(a.date);
            const c2 = dates.indexOf(date);
            if (r1 === undefined || r2 === undefined || c1 < 0 || c2 < 0) {
                return;
            }
            const next = new Set(baseSelection.current);
            for (let r = Math.min(r1, r2); r <= Math.max(r1, r2); r++) {
                for (let c = Math.min(c1, c2); c <= Math.max(c1, c2); c++) {
                    const row = events[r];
                    const day = dates[c];
                    if (row !== undefined && day !== undefined) {
                        next.add(eventCellKey(row.id, day));
                    }
                }
            }
            onSelectionChange(next);
        },
        [dates, events, rowIndex, onSelectionChange],
    );

    const handlePointerDown = useCallback(
        (eventId: number, date: IsoDate, additive: boolean) => {
            dragging.current = true;
            anchor.current = { eventId, date };
            baseSelection.current = additive ? new Set(selection) : new Set();
            const next = new Set(baseSelection.current);
            next.add(eventCellKey(eventId, date));
            onSelectionChange(next);
        },
        [selection, onSelectionChange],
    );

    const handleChipDragStart = useCallback((a: CalendarAssignment) => {
        // A chip drag and a selection drag are different gestures; starting one
        // must cancel the other or the grid ends up doing both at once.
        dragging.current = false;
        anchor.current = null;
        carrying.current = a;
        setIsCarrying(true);
    }, []);

    const handleChipDrop = useCallback(
        (eventId: number, date: IsoDate) => {
            const a = carrying.current;
            carrying.current = null;
            setIsCarrying(false);
            if (a === null || (a.eventId === eventId && a.date === date)) {
                return;
            }
            onMoveAssignment(a, eventId, date);
        },
        [onMoveAssignment],
    );

    useEffect(() => {
        function clear(): void {
            carrying.current = null;
            setIsCarrying(false);
        }
        window.addEventListener('dragend', clear);
        return () => {
            window.removeEventListener('dragend', clear);
        };
    }, []);

    const handlePointerEnter = useCallback(
        (eventId: number, date: IsoDate) => {
            if (!dragging.current) {
                return;
            }
            extendTo(eventId, date);
        },
        [extendTo],
    );

    // Released anywhere, including outside the grid — otherwise a drag that ends
    // over the toolbar leaves the grid stuck in select mode.
    useEffect(() => {
        function stop(): void {
            dragging.current = false;
            anchor.current = null;
        }
        window.addEventListener('pointerup', stop);
        window.addEventListener('pointercancel', stop);
        return () => {
            window.removeEventListener('pointerup', stop);
            window.removeEventListener('pointercancel', stop);
        };
    }, []);

    // The header row's tint, painted as a gradient layer over background.paper
    // for the same reason as the today tint: the header is sticky, so a
    // translucent background would let rows show through while scrolling.
    // From the active theme's primary, so every theme gets its own header tint.
    const headerTint = alpha(theme.palette.primary.main, 0.16);
    const tintLayer = (colour: string): string => `linear-gradient(${colour}, ${colour})`;

    const gridTemplate = `${String(jobCol)}px repeat(${String(dates.length)}, minmax(${String(dayCol)}px, 1fr))`;

    return (
        <Box
            sx={{
                overflow: 'auto',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                bgcolor: 'background.paper',
                // On a phone the toolbar wraps to several rows and scrolls away,
                // so the grid gets nearly the whole screen once you reach it.
                maxHeight: { xs: 'calc(100dvh - 120px)', md: 'calc(100vh - 250px)' },
                userSelect: 'none',
            }}
        >
            <Box sx={{ display: 'grid', gridTemplateColumns: gridTemplate, minWidth: 'fit-content' }}>
                {/* ─── Header ─────────────────────────────────────────────── */}
                <Box
                    sx={{
                        position: 'sticky',
                        top: 0,
                        left: 0,
                        zIndex: 3,
                        bgcolor: 'background.paper',
                        backgroundImage: tintLayer(headerTint),
                        borderRight: '2px solid',
                        borderBottom: '2px solid',
                        borderColor: 'divider',
                        px: 1.5,
                        py: 1,
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: '0.7rem',
                            fontWeight: 700,
                            letterSpacing: 0.5,
                            textTransform: 'uppercase',
                            color: 'text.secondary',
                        }}
                    >
                        Job
                    </Typography>
                </Box>

                {dates.map((d) => (
                    <Box
                        key={d}
                        sx={{
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            /* The today tint must sit ON an opaque base, not be
                               a translucent background: this header is sticky,
                               and rows scrolling underneath show straight
                               through any alpha. Painting the tint as a gradient
                               layer over background.paper keeps the highlight
                               and keeps the header opaque. */
                            bgcolor: 'background.paper',
                            backgroundImage:
                                d === today
                                    ? `${tintLayer(alpha(theme.palette.secondary.main, 0.2))}, ${tintLayer(headerTint)}`
                                    : tintLayer(headerTint),
                            borderRight: '1px solid',
                            borderBottom: '2px solid',
                            borderColor: 'divider',
                            textAlign: 'center',
                            py: 0.75,
                        }}
                    >
                        <Typography
                            sx={{
                                fontSize: '0.64rem',
                                color: isWeekend(d) ? 'text.disabled' : 'text.secondary',
                                lineHeight: 1.2,
                            }}
                        >
                            {formatDow(d)}
                        </Typography>
                        <Typography
                            sx={{
                                fontSize: '0.95rem',
                                fontWeight: 700,
                                lineHeight: 1.2,
                                color: d === today ? 'secondary.main' : 'text.primary',
                            }}
                        >
                            {formatDayNum(d)}
                        </Typography>
                    </Box>
                ))}

                {/* ─── Job rows, then the "Other" block ───────────────────── */}
                {events.map((event, i) => (
                    <Box key={event.id} sx={{ display: 'contents' }}>
                        {/* The divider between real jobs and non-job time. Drawn
                            on the first categorised row, so it appears only when
                            there is something below it to head. Spans every
                            column, which is why it sits inside display: contents
                            rather than wrapping the rows in their own element —
                            the grid must stay one grid for the sticky columns to
                            line up. */}
                        {event.categoryId !== null && (i === 0 || events[i - 1]?.categoryId === null) ? (
                            <Box
                                sx={{
                                    gridColumn: '1 / -1',
                                    borderTop: '2px solid',
                                    borderBottom: '1px solid',
                                    borderColor: 'divider',
                                    bgcolor: 'action.hover',
                                    py: 0.5,
                                }}
                            >
                                {/* The band spans every column, so the label
                                    itself is what sticks — otherwise it scrolls
                                    off to the left the moment you page through
                                    a month and the heading reads as a blank
                                    stripe. */}
                                <Typography
                                    sx={{
                                        position: 'sticky',
                                        left: 0,
                                        display: 'inline-block',
                                        pl: 1.5,
                                        pr: 2,
                                        fontSize: '0.7rem',
                                        fontWeight: 700,
                                        letterSpacing: 0.5,
                                        textTransform: 'uppercase',
                                        color: 'text.secondary',
                                    }}
                                >
                                    Other
                                </Typography>
                            </Box>
                        ) : null}
                        <Box
                            onClick={() => {
                                if (!readOnly) {
                                    onEditEvent(event);
                                }
                            }}
                            sx={{
                                cursor: readOnly ? 'default' : 'pointer',
                                position: 'sticky',
                                left: 0,
                                zIndex: 1,
                                bgcolor: 'background.paper',
                                '&:hover': { bgcolor: readOnly ? 'background.paper' : 'action.hover' },
                                borderRight: '2px solid',
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                borderLeft: '4px solid',
                                borderLeftColor: event.colour ?? 'transparent',
                                minHeight: MIN_ROW_H,
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'center',
                                px: { xs: 1, sm: 1.5 },
                                py: 0.75,
                                overflowWrap: 'anywhere',
                            }}
                        >
                            <Typography
                                sx={{ fontSize: { xs: '0.76rem', sm: '0.82rem' }, fontWeight: 700, color: 'success.light', lineHeight: 1.25 }}
                            >
                                {jobTitle(event, categoryNames)}
                            </Typography>
                            <Typography sx={{ fontSize: '0.7rem', color: 'text.secondary', lineHeight: 1.4 }}>
                                {[
                                    text(event.jobNumber) === null ? null : `Job ${String(text(event.jobNumber))}`,
                                    text(event.clientName),
                                ]
                                    .filter((v): v is string => v !== null)
                                    .join(' · ')}
                            </Typography>
                            {event.startsOn !== null && event.endsOn !== null ? (
                                <Typography sx={{ fontSize: '0.66rem', color: 'text.disabled', lineHeight: 1.4 }}>
                                    {formatShort(event.startsOn)} – {formatShort(event.endsOn)}
                                </Typography>
                            ) : null}
                        </Box>

                        {dates.map((d) => {
                            const key = eventCellKey(event.id, d);
                            const inWindow =
                                (event.startsOn === null || d >= event.startsOn) &&
                                (event.endsOn === null || d <= event.endsOn);
                            return (
                                <Cell
                                    key={key}
                                    eventId={event.id}
                                    date={d}
                                    assignments={cells.get(key) ?? EMPTY}
                                    staffById={staffById}
                                    clashes={clashes}
                                    selected={selection.has(key)}
                                    weekend={isWeekend(d)}
                                    inWindow={inWindow}
                                    onPointerDown={handlePointerDown}
                                    onPointerEnter={handlePointerEnter}
                                    minWidth={dayCol}
                                    readOnly={readOnly}
                                    onAddStaff={onAddStaff}
                                    onRemoveAssignment={onRemoveAssignment}
                                    onChipDragStart={handleChipDragStart}
                                    onChipDrop={handleChipDrop}
                                    dropTarget={isCarrying}
                                />
                            );
                        })}
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

/** Stable empty array, so an empty cell's props never change identity. */
const EMPTY: readonly CalendarAssignment[] = [];
